'use strict';

import axios from 'axios';
import { startSpinner, stopSpinner } from '@jscomponents/spinner';
import { listenDOMEvent, createElement } from '@jspartials/dom-api-utils';
import { gtmListenProductTiles, gtmApplyFilter } from '@jspartials/gtm-utils';
import { initMultiSelects } from '@jspages/search/multiSelect';
import { listenButtonShowMore } from '@jspages/search/loadMoreProducts';
import { listenSortingButtons } from '@jspages/search/sortingButtons';
import { updateCollaborationSelect } from '@jspages/search/collaborationSelect';
import { updateUrlLink, getAjaxUrlWithParams } from '@jspartials/filter-utils';
import { customEvent } from '@jspartials/general-utils';
import Recommendations from '@jsclasses/recommendations';
import { classes, addClassToElement } from '@jspartials/class-list-utils';
import { listenImagesCarousel } from '@jspages/search/imagesCarouselTiles';

// Selectors
const selector = {
  refinementBar: '.js-refine-bar',
  refinements: '.js-refinements',
  sortingButton: '.js-sorting-button',
  applyFilters: '.js-apply-filters',
  resetFilters: '.js-reset-filters',
  toggleFilters: '.js-toggle-filters',
  productTile: '.js-product-tile',
  recommendations: '.js-search-results__recommendations',
  productTileList: '.js-grid-products',
  productTile: '.js-plp-list__item',
  countdown: '.plp .js-countdown',
  seoContentWrapper: '.js-seo-content-wrapper'
};

// Elements
const element = {
  resetFilters: document.querySelector(selector.resetFilters) || null
};

// Variables
const eventReloadVtoButtons = new CustomEvent(customEvent.reloadVtoButtons);
const eventListenProductTiles = new CustomEvent(customEvent.listenProductTiles);
const eventInitProductTilesAnimation = new CustomEvent(
  customEvent.initProductTilesAnimation
);

// Functions

/**
 * Update DOM elements with Ajax results
 * @param {HTMLElement} results - DOM element
 * @param {String} selector - DOM element selector to look up in the results
 * @return {void}
 */
const updateDom = (results, selector) => {
  const currentDomElement = document.querySelector(selector) || null;
  const updateDomElement = results.querySelector(selector) || null;

  if (currentDomElement && updateDomElement) {
    currentDomElement.innerHTML = updateDomElement.innerHTML;
  }
};

/**
 * Parse Ajax results and updated select DOM elements
 * @param {String} response - HTML code
 * @return {void}
 */
const parseResults = (response) => {
  const resultHtml = createElement({
    tag: 'div',
    content: response
  });
  const selectorsToUpdate = [
    selector.refinements,
    '.js-grid-header',
    '.js-applied-filters',
    '.js-multiselect-wrapper',
    selector.productTileList,
    '.js-shortcut-filters',
    '.js-fixed-filter',
    '.js-seo-content-top',
    '.js-seo-content-bottom',
    '.js-button-see-results',
    '.js-message-results-found',
    '.js-button-show-filters',
    '.js-total-results-found',
    selector.resetFilters
  ];

  selectorsToUpdate.forEach((selector) => {
    updateDom(resultHtml, selector);
  });
};

/**
 * Scroll to product on load page
 * @returns {void}
 */
const scrollToProductOnInit = () => {
  if (
    typeof personalizedSorting !== 'undefined' &&
    typeof currentSorting !== 'undefined'
  ) {
    if (personalizedSorting == currentSorting) {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
      window.scrollTo(0, top);
      return;
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const productId = urlParams.get('product');
  const target = productId
    ? document.querySelector(
        `${selector.productTile} [data-pid="${productId}"]`
      )
    : null;
  if (target) {
    target.scrollIntoView({ block: 'center' });
  }
};

/**
 * Listen filters triggers clicks
 * @returns {void}
 */
const listenFiltersTriggers = () => {
  const filtersTriggers =
    document.querySelectorAll(selector.applyFilters) || [];
  const toggleFilters = document.querySelectorAll(selector.toggleFilters) || [];

  filtersTriggers.forEach((trigger) => {
    listenDOMEvent(trigger, 'click', () => {
      applyFilter(trigger);
    });
  });

  toggleFilters.forEach((trigger) => {
    listenDOMEvent(trigger, 'change', () => {
      applyFilter(trigger);
    });
  });
};

/**
 * Apply filter
 * @param {HTMLElement} button - Button to apply filter
 * @returns {void}
 */
const applyFilter = (button) => {
  const urlAjax = getAjaxUrlWithParams(button.dataset.params);
  const urlFinal = getUrlIncludingSortingRule(urlAjax);

  startSpinnerFilters(button);
  gtmApplyFilter(button);

  axios
    .get(urlFinal)
    .then((response) => {
      updateContent(response.data);
    })
    .catch((error) => {
      console.error(error);
    })
    .then(() => {
      stopSpinnerFilters(button);
    });
};

/**
 * Get url including sorting rule
 * @param {String} url - Url to get
 * @returns {String} Url including sorting rule
 */
const getUrlIncludingSortingRule = (url) => {
  const urlWithoutSortingRule = url.split('&srule=')[0];
  const currentSortingButton =
    document.querySelector(`${selector.sortingButton}`) || null;
  const currentSortingOption = currentSortingButton
    ? currentSortingButton.options[currentSortingButton.selectedIndex]
    : null;
  const sortingRuleId = currentSortingOption
    ? currentSortingOption?.dataset?.sortingRuleId
    : '';

  return sortingRuleId ? `${urlWithoutSortingRule}&srule=${sortingRuleId}` : '';
};

/**
 * Make an ajax request to search
 * @param {String} url Url to call
 * @returns {void}
 */
const ajaxRequestToSearch = (url) => {
  const urlFinal = getUrlIncludingSortingRule(url);

  if (urlFinal) {
    startSpinner();

    axios
      .get(urlFinal)
      .then((response) => {
        updateContent(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .then(() => {
        stopSpinner();
      });
  }
};

/**
 * Update content of the page after ajax request
 * @param {String} content - HTML code
 * @returns {void}
 */
const updateContent = (content) => {
  parseResults(content);
  listenImagesCarousel();
  listenFiltersTriggers();
  initMultiSelects();
  listenSortingButtons();
  listenButtonShowMore();
  updateUrlLink();
  updateCollaborationSelect();
  gtmListenProductTiles(true);
  document.dispatchEvent(eventListenProductTiles);
  document.dispatchEvent(eventReloadVtoButtons);
  document.dispatchEvent(eventInitProductTilesAnimation);
};

/**
 * Start spinner over filters
 * @param {HTMLElement} element - Element that triggers the spinner
 * @returns {void}
 */
const startSpinnerFilters = (element) => {
  const isInsideRefinements = element.closest(selector.refinementBar);

  if (isInsideRefinements) {
    startSpinner(selector.refinements);
  } else {
    startSpinner();
  }
};

/**
 * Stop spinner over filters
 * @param {HTMLElement} element - Element that triggers the spinner
 * @returns {void}
 */
const stopSpinnerFilters = (element) => {
  const isInsideRefinements = element.closest(selector.refinements);

  if (isInsideRefinements) {
    stopSpinner(selector.refinements);
  } else {
    stopSpinner();
  }
};

/**
 * Init recommendations
 * @returns {void}
 */
const initRecommendations = () => {
  const recommendations =
    document.querySelector(selector.recommendations) || null;

  if (recommendations) {
    new Recommendations({
      wrapper: recommendations,
      showPrice: true,
      maxVisibleItems: 5
    });
  }
};

const initProductTilesAnimation = () => {
  const productTiles = document.querySelectorAll(
    `${selector.productTileList} ${selector.productTile}:not(.${classes.is.animated})`
  );

  const productTileKeyframe = [
    { transform: 'translateY(60px)', opacity: '0' },
    { transform: 'translateY(0px)', opacity: '1' }
  ];

  productTiles.forEach((productTile, i) => {
    productTile.animate(productTileKeyframe, {
      duration: 600,
      delay: 100,
      easing: 'ease',
      fill: 'both'
    });

    addClassToElement(productTile, classes.is.animated);
  });
};

/**
 * Move countdown from the slot to the resultsNoDecorator title in the PLP
 * @returns {void}
 */

const moveCountdownNextToTitle = () => {
  const countdown = document.querySelector(selector.countdown);

  if (countdown) {
    const wrapper = document.querySelector(selector.seoContentWrapper);
    wrapper && wrapper.appendChild(countdown);
  }
};

// Init
document.addEventListener('DOMContentLoaded', function () {
  // Scroll to product on load page
  scrollToProductOnInit();

  // Update collaboration select
  updateCollaborationSelect();

  // Init multi selects
  initMultiSelects();

  // Listen filters triggers clicks
  listenFiltersTriggers();

  // Listen sort menu
  listenSortingButtons();

  // Load more products
  listenButtonShowMore();

  // Init recommendations
  initRecommendations();

  // Init product tiles animation
  initProductTilesAnimation();

  // Move countdown next to title
  moveCountdownNextToTitle();

  // Listen images carousel
  listenImagesCarousel();

  // Listen reset filters button
  if (element.resetFilters) {
    listenDOMEvent(element.resetFilters, 'click', () => {
      applyFilter(element.resetFilters);
      element.resetFilters.blur();
    });
  }

  // Listen custom event to make ajax request to search
  document.addEventListener('sendAjaxRequestToSearch', (event) => {
    const details = event.detail;
    const url = details?.url || null;

    if (url) {
      ajaxRequestToSearch(url);
    }
  });

  // Listen custom event to init product tiles animation
  document.addEventListener(
    customEvent.initProductTilesAnimation,
    initProductTilesAnimation
  );
});
